import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { EASE } from "../../lib/motion";
import { scListRankings, scGetRankingResults } from "../../lib/sifcontrolApi";

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(2)}%`);
const fmtRatio = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2));

export default function SifControlRankings() {
    const [methods, setMethods] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingResults, setLoadingResults] = useState(false);

    useEffect(() => {
        scListRankings()
            .then((r) => {
                setMethods(r.items || []);
                if ((r.items || []).length > 0) setSelectedId(r.items[0].id);
            })
            .catch(() => setMethods([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!selectedId) return;
        setLoadingResults(true);
        scGetRankingResults(selectedId)
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoadingResults(false));
    }, [selectedId]);

    const selected = useMemo(() => methods.find((m) => m.id === selectedId), [methods, selectedId]);

    return (
        <section className="py-14 md:py-20" data-testid="sc-rankings-page">
            <div className="mx-auto max-w-[1240px] px-4 md:px-6">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-oth-ink3 font-body">Rankings</div>
                    <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-oth-ink">SIF Rankings</h1>
                    <p className="mt-4 max-w-[620px] text-oth-ink2 font-body text-base md:text-lg">
                        See which SIFs score highest based on different methodologies. Change the methodology to reorder.
                    </p>
                </motion.div>

                {loading ? (
                    <div className="mt-12 p-8 text-center text-sm font-body text-oth-ink3">Loading methodologies…</div>
                ) : methods.length === 0 ? (
                    <div className="mt-12 rounded-3xl border border-dashed border-oth-line bg-white p-10 text-center text-sm font-body text-oth-ink3" data-testid="sc-rankings-empty">
                        No active ranking methodologies. Ask your administrator to publish one.
                    </div>
                ) : (
                    <>
                        <div className="mt-10 flex flex-wrap gap-2" data-testid="sc-rankings-methods">
                            {methods.map((m) => (
                                <button
                                    key={m.id}
                                    onClick={() => setSelectedId(m.id)}
                                    className={`btn-pill !text-xs !py-2 !px-4 ${selectedId === m.id ? "btn-pill-dark" : ""}`}
                                    data-testid={`sc-rankings-method-${m.id}`}
                                >
                                    {m.name}
                                </button>
                            ))}
                        </div>

                        {selected?.description && (
                            <div className="mt-4 text-sm font-body text-oth-ink3 max-w-[720px]" data-testid="sc-rankings-desc">{selected.description}</div>
                        )}

                        <div className="mt-6 rounded-3xl border border-oth-line bg-white overflow-hidden">
                            {loadingResults ? (
                                <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading rankings…</div>
                            ) : !data || (data.results || []).length === 0 ? (
                                <div className="p-10 text-center text-sm font-body text-oth-ink3" data-testid="sc-rankings-results-empty">
                                    Not enough data yet. Rankings appear once at least 3 months of NAV history is ingested for a few SIFs and this methodology has been computed.
                                </div>
                            ) : (
                                <div>
                                    <div className="grid grid-cols-[50px_1.4fr_120px_100px_100px_100px_100px] text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body px-5 py-3 bg-oth-bg-2/60 border-b border-oth-line">
                                        <div>Rank</div><div>SIF</div><div>Strategy</div><div>1Y</div><div>Sharpe</div><div>Drawdown</div><div className="text-right">Rating</div>
                                    </div>
                                    <div className="divide-y divide-oth-line" data-testid="sc-rankings-list">
                                        {(data.results || []).map((r) => (
                                            <Link
                                                to={`/sifcontrol/sif/${r.sif_id}`}
                                                key={r.sif_id}
                                                className="grid grid-cols-[50px_1.4fr_120px_100px_100px_100px_100px] items-center px-5 py-3.5 hover:bg-oth-bg-2 transition-colors"
                                                data-testid={`sc-rankings-row-${r.sif_id}`}
                                            >
                                                <div className="text-xs font-body text-oth-ink3">#{r.rank}</div>
                                                <div>
                                                    <div className="font-display text-sm md:text-base font-medium text-oth-ink truncate">{r.sif_name}</div>
                                                    <div className="text-[11px] font-body text-oth-ink3 mt-0.5">{r.sif_code}</div>
                                                </div>
                                                <div className="text-xs font-body text-oth-ink2 truncate">{r.strategy_category || "—"}</div>
                                                <div className="text-xs font-body text-oth-ink">{fmtPct(r.preview_metrics?.return_1y)}</div>
                                                <div className="text-xs font-body text-oth-ink">{fmtRatio(r.preview_metrics?.sharpe)}</div>
                                                <div className="text-xs font-body text-oth-ink">{fmtPct(r.preview_metrics?.max_drawdown)}</div>
                                                <div className="text-sm font-body text-oth-ink text-right">{r.star_label}</div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-4 text-[11px] font-body text-oth-ink3 max-w-[720px]" data-testid="sc-rankings-disclaimer">
                            Rankings are based on the selected methodology and historical data. Changing the weights or parameters may change the ranking. Past performance does not indicate future results.
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
